import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { supabase } from '../../../../../lib/supabase-auth'
import { useAuth } from '../../../../../contexts/AuthContextLazy'
import { showError, showSuccess } from '../../../../../utils/toast'
import { 
  ArrowLeftIcon,
  ArrowsRightLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  LinkIcon,
  UserGroupIcon,
  ArrowPathIcon,
  InformationCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'

interface PortalClient {
  id: string
  name: string
  email: string
  mapped: boolean
  mappedTo?: string
}

interface MyMeetingsParty {
  id: string
  name: string
  email: string
  type: string
  lastActivity?: string
}

interface ClientMappingData {
  id: string
  portal_client_id: string
  mymeetings_party_id: string
  mymeetings_party_name: string
  confidence_score: number
  mapped_at: string
  mapped_by: string
}

interface SuggestedMapping {
  portalClientId: string
  myMeetingsPartyId: string
  confidence: number
  reason: string
}

export const ClientMapping: React.FC = () => {
  const navigate = useNavigate()
  const { hasRole, user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [portalClients, setPortalClients] = useState<PortalClient[]>([])
  const [myMeetingsParties, setMyMeetingsParties] = useState<MyMeetingsParty[]>([])
  const [mappings, setMappings] = useState<ClientMappingData[]>([])
  const [suggestions, setSuggestions] = useState<SuggestedMapping[]>([])
  const [searchPortal, setSearchPortal] = useState('')
  const [searchMyMeetings, setSearchMyMeetings] = useState('')
  const [selectedPortalClient, setSelectedPortalClient] = useState<string | null>(null)
  const [selectedMyMeetingsParty, setSelectedMyMeetingsParty] = useState<string | null>(null)
  const [autoMapping, setAutoMapping] = useState(false)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      // Load portal clients
      const { data: orgs } = await supabase
        .from('organizations')
        .select('id, name, settings')
        .order('name')

      // Load existing mappings
      const { data: mappingData } = await supabase
        .from('client_mymeetings_mapping')
        .select('*')

      // Simulate loading MyMeetings parties (in production, this would be an API call)
      const mockParties: MyMeetingsParty[] = [
        { id: 'party-1', name: 'Acme Corporation', email: 'contact@acme.com', type: 'Corporate' },
        { id: 'party-2', name: 'Smith Family Trust', email: 'john@smithtrust.com', type: 'Trust' },
        { id: 'party-3', name: 'Johnson Holdings', email: 'info@johnsonholdings.com', type: 'Corporate' },
        { id: 'party-4', name: 'Williams Investment Group', email: 'contact@wig.com', type: 'Investment' },
        { id: 'party-5', name: 'Brown Enterprises', email: 'admin@brownent.com', type: 'Corporate' },
        { id: 'party-6', name: 'Davis Foundation', email: 'info@davisfoundation.org', type: 'Foundation' },
        { id: 'party-7', name: 'Miller & Associates', email: 'contact@millerassoc.com', type: 'Partnership' },
        { id: 'party-8', name: 'Wilson Retirement Fund', email: 'admin@wilsonretirement.com', type: 'Retirement' }
      ]

      if (orgs && mappingData) {
        // Process portal clients with mapping status
        const clientsWithMapping = orgs.map(org => ({
          id: org.id,
          name: org.name,
          email: org.settings?.primary_contact?.email || '',
          mapped: mappingData.some(m => m.portal_client_id === org.id),
          mappedTo: mappingData.find(m => m.portal_client_id === org.id)?.mymeetings_party_id
        }))

        setPortalClients(clientsWithMapping)
        setMyMeetingsParties(mockParties)
        setMappings(mappingData)

        // Generate suggestions
        generateSuggestions(clientsWithMapping, mockParties, mappingData)
      }
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error loading data:', error);
        }

      }
    } finally {
      setLoading(false)
    }
  }

  const generateSuggestions = (
    clients: PortalClient[], 
    parties: MyMeetingsParty[], 
    existingMappings: ClientMappingData[]
  ) => {
    const newSuggestions: SuggestedMapping[] = []

    clients.forEach(client => {
      if (!client.mapped) {
        // Try email matching first
        const emailMatch = parties.find(p => 
          p.email.toLowerCase() === client.email.toLowerCase() &&
          !existingMappings.some(m => m.mymeetings_party_id === p.id)
        )
        
        if (emailMatch) {
          newSuggestions.push({
            portalClientId: client.id,
            myMeetingsPartyId: emailMatch.id,
            confidence: 0.95,
            reason: 'Email match'
          })
        } else {
          // Try name similarity
          const nameParts = client.name.toLowerCase().split(/\s+/)
          const nameMatch = parties.find(p => {
            const partyNameLower = p.name.toLowerCase()
            return nameParts.some(part => partyNameLower.includes(part)) &&
              !existingMappings.some(m => m.mymeetings_party_id === p.id)
          })

          if (nameMatch) {
            newSuggestions.push({
              portalClientId: client.id,
              myMeetingsPartyId: nameMatch.id,
              confidence: 0.7,
              reason: 'Name similarity'
            })
          }
        }
      }
    })

    setSuggestions(newSuggestions)
  }

  const handleCreateMapping = async (portalClientId: string, myMeetingsPartyId: string) => {
    try {
      const party = myMeetingsParties.find(p => p.id === myMeetingsPartyId)
      
      await supabase
        .from('client_mymeetings_mapping')
        .insert({
          portal_client_id: portalClientId,
          mymeetings_party_id: myMeetingsPartyId,
          mymeetings_party_name: party?.name || 'Unknown',
          mymeetings_org_id: 'pcm-financial', // This would come from the integration credentials
          confidence_score: 1.0, // Manual mapping = 100% confidence
          mapped_by: user?.id
        })

      // Refresh data
      await loadData()
      setSelectedPortalClient(null)
      setSelectedMyMeetingsParty(null)
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error creating mapping:', error);
        }

      }
      showError('Failed to create mapping')
    }
  }

  const handleRemoveMapping = async (mappingId: string) => {
    if (!confirm('Are you sure you want to remove this mapping?')) return

    try {
      await supabase
        .from('client_mymeetings_mapping')
        .delete()
        .eq('id', mappingId)

      await loadData()
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error removing mapping:', error);
        }

      }
      showError('Failed to remove mapping')
    }
  }

  const handleAutoMap = async () => {
    setAutoMapping(true)
    
    try {
      // Map all high confidence suggestions
      const highConfidenceSuggestions = suggestions.filter(s => s.confidence >= 0.9)
      
      for (const suggestion of highConfidenceSuggestions) {
        const party = myMeetingsParties.find(p => p.id === suggestion.myMeetingsPartyId)
        
        await supabase
          .from('client_mymeetings_mapping')
          .insert({
            portal_client_id: suggestion.portalClientId,
            mymeetings_party_id: suggestion.myMeetingsPartyId,
            mymeetings_party_name: party?.name || 'Unknown',
            mymeetings_org_id: 'pcm-financial',
            confidence_score: suggestion.confidence,
            mapped_by: user?.id
          })
      }

      await loadData()
      showSuccess(`Successfully mapped ${highConfidenceSuggestions.length} clients`)
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error auto-mapping:', error);
        }

      }
      showError('Failed to auto-map clients')
    } finally {
      setAutoMapping(false)
    }
  }

  // Filtered lists based on search
  const filteredPortalClients = useMemo(() => 
    portalClients.filter(client => 
      client.name.toLowerCase().includes(searchPortal.toLowerCase()) ||
      client.email.toLowerCase().includes(searchPortal.toLowerCase())
    ), [portalClients, searchPortal]
  )

  const filteredMyMeetingsParties = useMemo(() =>
    myMeetingsParties.filter(party =>
      party.name.toLowerCase().includes(searchMyMeetings.toLowerCase()) ||
      party.email.toLowerCase().includes(searchMyMeetings.toLowerCase())
    ), [myMeetingsParties, searchMyMeetings]
  )

  // Available parties (not yet mapped)
  const availableParties = useMemo(() =>
    filteredMyMeetingsParties.filter(party =>
      !mappings.some(m => m.mymeetings_party_id === party.id)
    ), [filteredMyMeetingsParties, mappings]
  )

  if (!hasRole(['super_admin', 'pcm_staff'])) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">You don't have permission to view this page.</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <ArrowPathIcon className="h-8 w-8 text-gray-400 animate-spin" />
      </div>
    )
  }

  const unmappedCount = portalClients.filter(c => !c.mapped).length
  const highConfidenceSuggestions = suggestions.filter(s => s.confidence >= 0.9)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link
          to="/portal/admin/integrations/mymeetings"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="mr-2 h-4 w-4" />
          Back to MyMeetings
        </Link>
        <div className="mt-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Client Mapping</h1>
            <p className="mt-1 text-sm text-gray-500">
              Map your portal clients to MyMeetings parties for data synchronization
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <span className="text-sm text-gray-500">
              {portalClients.length - unmappedCount} of {portalClients.length} clients mapped
            </span>
            {highConfidenceSuggestions.length > 0 && (
              <button
                onClick={handleAutoMap}
                disabled={autoMapping}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50"
              >
                {autoMapping ? (
                  <>
                    <ArrowPathIcon className="mr-2 h-4 w-4 animate-spin" />
                    Mapping...
                  </>
                ) : (
                  <>
                    <LinkIcon className="mr-2 h-4 w-4" />
                    Auto-map {highConfidenceSuggestions.length} High Confidence
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <InformationCircleIcon className="h-5 w-5 text-amber-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-amber-800">
                {suggestions.length} Suggested Mapping{suggestions.length !== 1 ? 's' : ''} Found
              </h3>
              <div className="mt-2 text-sm text-amber-700">
                <p>
                  We found potential matches based on email addresses and name similarity.
                  Review and apply these mappings to save time.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mapping Interface */}
      <div className="bg-white portal-content-shadow-sm rounded-lg border border-gray-200">
        <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-gray-200">
          {/* Portal Clients Column */}
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Portal Clients</h3>
            <div className="mb-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search clients..."
                  value={searchPortal}
                  onChange={(e) => setSearchPortal(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500"
                />
                <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            </div>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filteredPortalClients.map(client => (
                <div
                  key={client.id}
                  onClick={() => !client.mapped && setSelectedPortalClient(client.id)}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    client.mapped 
                      ? 'border-green-200 bg-green-50' 
                      : selectedPortalClient === client.id
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-gray-200 hover:border-gray-300'
                  } ${client.mapped ? 'cursor-not-allowed opacity-60' : ''}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{client.name}</p>
                      <p className="text-xs text-gray-500">{client.email}</p>
                    </div>
                    {client.mapped && (
                      <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Suggestions Column */}
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Suggested Matches</h3>
            {selectedPortalClient ? (
              <div className="space-y-3">
                {suggestions
                  .filter(s => s.portalClientId === selectedPortalClient)
                  .map(suggestion => {
                    const party = myMeetingsParties.find(p => p.id === suggestion.myMeetingsPartyId)
                    return (
                      <div
                        key={suggestion.myMeetingsPartyId}
                        className="p-3 rounded-lg border border-amber-200 bg-amber-50"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <p className="text-sm font-medium text-gray-900">{party?.name}</p>
                          <span className="text-xs text-amber-700">{suggestion.reason}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="flex-1 bg-gray-200 rounded-full h-2 w-20 mr-2">
                              <div
                                className="bg-amber-500 h-2 rounded-full"
                                style={{ width: `${suggestion.confidence * 100}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-500">
                              {Math.round(suggestion.confidence * 100)}%
                            </span>
                          </div>
                          <button
                            onClick={() => handleCreateMapping(selectedPortalClient, suggestion.myMeetingsPartyId)}
                            className="text-xs text-amber-600 hover:text-amber-900"
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )
                  })}
                {selectedPortalClient && selectedMyMeetingsParty && (
                  <div className="mt-4 text-center">
                    <button
                      onClick={() => handleCreateMapping(selectedPortalClient, selectedMyMeetingsParty)}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-amber-600 hover:bg-amber-700"
                    >
                      <ArrowsRightLeftIcon className="mr-2 h-4 w-4" />
                      Create Mapping
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center mt-8">
                Select a portal client to see suggestions
              </p>
            )}
          </div>

          {/* MyMeetings Parties Column */}
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">MyMeetings Parties</h3>
            <div className="mb-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search parties..."
                  value={searchMyMeetings}
                  onChange={(e) => setSearchMyMeetings(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500"
                />
                <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            </div>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {availableParties.map(party => (
                <div
                  key={party.id}
                  onClick={() => setSelectedMyMeetingsParty(party.id)}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedMyMeetingsParty === party.id
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <p className="text-sm font-medium text-gray-900">{party.name}</p>
                  <p className="text-xs text-gray-500">{party.email}</p>
                  <p className="text-xs text-gray-400 mt-1">{party.type}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Existing Mappings */}
      <div className="bg-white portal-content-shadow-sm rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Existing Mappings</h3>
        </div>
        <div className="overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Portal Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  MyMeetings Party
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Confidence
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mapped On
                </th>
                <th className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {mappings.map((mapping) => {
                const client = portalClients.find(c => c.id === mapping.portal_client_id)
                return (
                  <tr key={mapping.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {client?.name || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {mapping.mymeetings_party_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2 max-w-[60px]">
                          <div
                            className="bg-green-500 h-2 rounded-full"
                            style={{ width: `${mapping.confidence_score * 100}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-500">
                          {Math.round(mapping.confidence_score * 100)}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(mapping.mapped_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleRemoveMapping(mapping.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}